import proctoringSessionsSlice, { ProctoringEventQueryOptions, ProctoringEventType } from "./slices/proctoringSession";
import { useContext } from "react";
import { ProctoringResultsContext } from "../context";
import { SubscriptionOptions } from "@reduxjs/toolkit/query";
import { FILTER_EVENTS } from "../../student-selection/videoplayer/Timestamps/Selector/EventTypeSelector";

export const { useGetAssessmentQuery, useGetUserQuery } = proctoringSessionsSlice;

export function useGetProctoringSessionQuery() {
	const { proctoringSessionId } = useContext(ProctoringResultsContext);
	return proctoringSessionsSlice.useGetProctoringSessionQuery(proctoringSessionId);
}

export function useGetProctoringEventsQuery(
	{ flagLevels = [], eventTypes = [] }: Partial<ProctoringEventQueryOptions> = {},
	options?: SubscriptionOptions,
) {
	const { config } = useContext(ProctoringResultsContext);
	const { courseId, proctoringSessionId } = useContext(ProctoringResultsContext);
	let flagLevelsQuery = flagLevels;
	if (config.showFlaggedOnly) {
		if (flagLevels.length === 0) {
			flagLevelsQuery = [1, 2, 3];
		} else {
			flagLevelsQuery = flagLevels.filter((flagLevel) => flagLevel);
		}
	}
	let eventTypesQuery: ProctoringEventType[] = eventTypes;
	if (config.sessionInProgress) {
		if (eventTypes.length === 0) {
			eventTypesQuery = FILTER_EVENTS.filter(
				({ name }) => name !== "All Events" && name !== "No Submission" && name !== "Upload Incomplete",
			).map(({ name }) => name);
		} else {
			eventTypesQuery = eventTypes.filter(
				(type) => type !== "All Events" && type !== "No Submission" && type !== "Upload Incomplete",
			);
		}
	}
	return proctoringSessionsSlice.useGetProctoringEventsQuery(
		{
			courseId,
			sessionId: proctoringSessionId,
			flagLevels: flagLevelsQuery,
			eventTypes: eventTypesQuery,
		},
		{ ...options, refetchOnMountOrArgChange: true },
	);
}

export function useGetVideoUrlsQuery(type: "webcam" | "screencapture", options?: SubscriptionOptions) {
	const { courseId, proctoringSessionId } = useContext(ProctoringResultsContext);
	return proctoringSessionsSlice.useGetVideoUrlsQuery(
		{
			courseId,
			sessionId: proctoringSessionId,
			recordingType: type,
		},
		options,
	);
}

type ResolveFlagMutationReturnType = ReturnType<typeof proctoringSessionsSlice.useResolveFlagMutation>;

export function useResolveFlagMutation(): [
	(args: { eventId: number; comment?: string }) => ReturnType<ResolveFlagMutationReturnType[0]>,
	ResolveFlagMutationReturnType[1],
] {
	const [mutation, ...rest] = proctoringSessionsSlice.useResolveFlagMutation();
	const { proctoringSessionId } = useContext(ProctoringResultsContext);
	return [(args) => mutation({ sessionId: proctoringSessionId, ...args }), ...rest];
}
